import { state } from '@angular/animations';
import { Action, createReducer, on } from '@ngrx/store';
import { ClosureDetail, ClosureQueryParams, ClosureQueryResult, FlatterFilesResponse, closureQueryParmsFactory } from '../../models';
import { LookupValue } from '../../models/lookup.model';

import * as fromActions from '../actions/closures.actions';

export const closuresFeatureKey = 'closures';




export enum Loaders {
  loadingClosures = 'Loading Closures...',
  loadingDetail = 'Loading Detail...',
  loadinglookup = 'Loading lookups',
  loadingDwg = "loadingDwg",
  loadingAdditionalClosureDrawings = "loadingAdditionalClosureDrawings"
}

export interface State {
  flangeClasses: LookupValue[];
  filter?: ClosureQueryParams;
  filteredProducts: ClosureQueryResult[];
  diameters: LookupValue[];
  designCodes: LookupValue[];
  designFactors: LookupValue[];
  wallThicknesses: LookupValue[];
  hingeOrientations: LookupValue[];
  pressureRatings: LookupValue[];
  clampRings: LookupValue[];
  classRatings: LookupValue[];

  activeClosureId?: number | null;
  activeClosureDetail?: ClosureDetail;
  loaders: Loaders[];
  drawingDictionary: { [key: string]: FlatterFilesResponse };
}

export const initialState: State = {
  filter: closureQueryParmsFactory({ designPressureUnit: 1 }),
  filteredProducts: [],
  loaders: [],
  diameters: [],
  designCodes: [],
  designFactors: [],
  wallThicknesses: [],
  hingeOrientations: [],
  pressureRatings: [],
  clampRings: [],
  flangeClasses: [],
  classRatings: [],
  drawingDictionary: {}
};

export const reducer = createReducer(
  initialState,
  on(fromActions.loadClosureQueryResult, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadingClosures)
  })),
  on(fromActions.loadClosureQueryResultSuccess, (state, action) => ({
    ...state,
    filteredProducts: action.res,
    loaders: state.loaders.filter(f => f !== Loaders.loadingClosures)
  })),
  on(fromActions.loadClosureDetail, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadingDetail)
  })),
  on(fromActions.loadClosureDetailSuccess, (state, action) => ({
    ...state,
    activeClosureDetail: action.detail,
    loaders: state.loaders.filter(f => f !== Loaders.loadingDetail)
  })),
  on(fromActions.loadClosureDetailFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadingDetail)
  })),
  on(fromActions.loadClosureLookups, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadinglookup)
  })),
  on(fromActions.loadClosureLookupsSuccess, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadinglookup),
    diameters: action.diameters,
    designCodes: action.designCodes,
    designFactors: action.designFactors,
    wallThicknesses: action.wallThicknesses,
    hingeOrientations: action.hingeOrientations,
    pressureRatings: action.pressureRatings,
    clampRings: action.clampRings,
    flangeClasses: action.flangeClasses,
    classRatings: action.classRatings
  })),
  on(fromActions.changeDiameter, (state, action) => ({
    ...state,
    filter: {
      ...state.filter,
      nominalSize: action.diameter
    }
  })),
  on(fromActions.changeClampRing, (state, action) => ({
    ...state,
    filter: {
      ...state.filter,
      clampRingId: action.clampRingId
    }
  })),
  on(fromActions.changeFlanged, (state, action) => ({
    ...state,
    filter: {
      ...state.filter,
      flanged: action.flanged,
      classRatingId: action.flanged ? undefined : state.filter?.classRatingId,
      flangeClassId: action.flanged ? state.filter?.flangeClassId : undefined
    }
  })),
  on(fromActions.changeDesignPressure, (state, action) => ({
    ...state,
    filter: {
      ...state.filter,
      maxDesignPressure: action.designPressure
    }
  })),
  on(fromActions.changeDesignPressureUnit, (state, action) => ({
    ...state,
    filter: {
      ...state.filter,
      designPressureUnit: action.designPressureUnit
    }
  })),
  on(fromActions.changeDesignTempMin, (state, action) => ({
    ...state,
    filter: {
      ...state.filter,
      designTempMin: action.designTempMin
    }
  })),
  on(fromActions.changeDesignTempMax, (state, action) => ({
    ...state,
    filter: {
      ...state.filter,
      designTempMax: action.designTempMax
    }
  })),
  on(fromActions.changeHingeOrientation, (state, action) => ({
    ...state,
    filter: {
      ...state.filter,
      hingeOrientation: action.hingeOrientation || -1
    }
  })),
  on(fromActions.changeDesignCodeId, (state, action) => ({
    ...state,
    filter: {
      ...state.filter,
      designCodeId: action.designCodeId
    }
  })),
  on(fromActions.changeDesignFactorId, (state, action) => ({
    ...state,
    filter: {
      ...state.filter,
      designFactorId: action.designFactorId
    }
  })),
  on(fromActions.changeFlangeClass, (state, action) => ({
    ...state,
    filter: {
      ...state.filter,
      flangeClassId: action.flangeClassId
    }
  })),
  on(fromActions.changeClassRating, (state, action) => ({
    ...state,
    filter: {
      ...state.filter,
      classRatingId: action.classRatingId
    }
  })),
  on(fromActions.activateClosure, (state, action) => ({
    ...state,
    activeClosureId: action.id,
    loaders: state.loaders.concat(Loaders.loadingDetail)
  })),
  on(fromActions.clearClosure, (state, action) => ({
    ...state,
    activeClosureId: null,
    activeClosureDetail: undefined
  })),
  on(fromActions.loadDrawingFromFlatterFiles, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadingDwg)
  })),
  on(fromActions.loadClosureDrawing, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadingDwg)
  })),
  on(fromActions.loadClosureDrawingSuccess, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadingDwg)
  })),
  on(fromActions.loadClosureDrawingFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadingDwg)
  })),
  on(fromActions.loadActiveClosureDrawingDictionary, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadingAdditionalClosureDrawings)
  })),
  on(fromActions.loadActiveClosureDrawingDictionarySuccess, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadingAdditionalClosureDrawings),
    drawingDictionary: action.dictionary
  })),
  on(fromActions.loadActiveClosureDrawingDictionaryFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadingAdditionalClosureDrawings)
  })),
);
